import { ReactNode } from 'react';

import type { RunStatsSchema } from '@/types/schema';

export interface FilterComp {
  id: string;
  label: ReactNode;
  multiple?: boolean;
  valueType?: 'string' | 'number' | 'boolean';
  numericLabel?: ReactNode;
}

type FilterFieldValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number | boolean>
  | undefined;

interface BaseFilterField {
  label: FilterLabel | string;
  icon?: ReactNode;
  suffix?: string;
  disabled?: (value: { runStats: RunStatsSchema | undefined }) => boolean;
  disabledTooltip?: string;
  metadata?: Record<string, unknown>;
}

export interface CustomFilterField extends BaseFilterField {
  fieldComponent: (props: {
    value:
      | {
          comparator?: string;
          identifier?: string | undefined;
          argument?: FilterFieldValue;
        }
      | undefined;
    onChange: (value: {
      comparator?: string;
      identifier?: string | undefined;
      argument?: FilterFieldValue;
    }) => void;
    readOnly?: boolean;
  }) => ReactNode;
  name: string;
}

export interface StandardFilterField extends BaseFilterField {
  id: string;
  defaultComp: FilterComp;
  availableComps: FilterComp[];
  valueType: 'string' | 'number' | 'boolean';
}

export type FilterField = CustomFilterField | StandardFilterField;

export interface FilterGroup {
  label: FilterLabel;
  group: string;
  icon?: ReactNode;
  fields: StandardFilterField[];
  disabled?: (value: { runStats: RunStatsSchema | undefined }) => boolean;
  disabledTooltip?: string;
  omitKeyLabel?: boolean;
}

export enum FilterLabel {
  search = 'Full-Text Search',
  inputs = 'Input',
  outputs = 'Output',
  input_kv = 'Input Key',
  output_kv = 'Output Key',
  extra_kv = 'Extra Key',
  is_root = 'Is Trace',
  name = 'Run Name',
  error = 'Error Message',
  run_type = 'Run Type',
  latency = 'Latency',
  status = 'Status',
  tag = 'Tag',
  metadata = 'Metadata',
  feedback = 'Feedback',
  feedback_score = 'Feedback Score',
  feedback_value = 'Feedback Value',
  feedback_source = 'Feedback Source',
  run_id = 'Run ID',
  trace_id = 'Trace ID',
  key = 'Key',
  value = 'Value',
  score = 'Score',
  trace_count = 'Trace Count',
  thread_id = 'Thread ID',
}

export type FilterAnyField = FilterField | FilterGroup;

export const filterCompsMap = {
  eq: {
    id: 'eq',
    label: 'is',
    numericLabel: <span className="font-mono">{`=`}</span>,
  },
  gt: { id: 'gt', label: <span className="font-mono">{`>`}</span> },
  gte: { id: 'gte', label: <span className="font-mono">{`>=`}</span> },
  lt: { id: 'lt', label: <span className="font-mono">{`<`}</span> },
  lte: { id: 'lte', label: <span className="font-mono">{`<=`}</span> },
  neq: {
    id: 'neq',
    label: 'is not',
    numericLabel: <span className="font-mono">{`≠`}</span>,
  },
  like: { id: 'like', label: 'contains' },
  notlike: { id: 'notlike', label: 'does not contain' },
  in: { id: 'in', label: 'is one of', multiple: true },
  exists: { id: 'exists', label: 'exists', valueType: 'boolean' },
  contains: { id: 'eq', label: 'contains' },

  // deprecated, used for tags
  has: { id: 'has', label: 'has' },
} satisfies Record<string, FilterComp>;

export const filterCompsString: FilterComp[] = [
  filterCompsMap.eq,
  filterCompsMap.neq,
  filterCompsMap.like,
  filterCompsMap.notlike,
  filterCompsMap.in,
];

export const filterNumericRanges: FilterComp[] = [
  filterCompsMap.gt,
  filterCompsMap.gte,
  filterCompsMap.lt,
  filterCompsMap.lte,
];
export const filterNumericsExact: FilterComp[] = [
  filterCompsMap.eq,
  filterCompsMap.neq,
];

export const filterComps: FilterComp[] = [
  filterCompsMap.eq,
  filterCompsMap.like,
  filterCompsMap.notlike,
  filterCompsMap.neq,
  filterCompsMap.in,
  filterCompsMap.exists,
  filterCompsMap.gt,
  filterCompsMap.gte,
  filterCompsMap.lt,
  filterCompsMap.lte,
];

export const filterCompsBool: FilterComp[] = [filterCompsMap.eq];

export const PRESET_ITEM_CLASS_NAMES =
  'flex flex-shrink-0 items-center gap-1.5 px-2 py-1 text-sm font-medium text-tertiary transition-all hover:bg-tertiary';
export const ACTIVE_CLASS_NAMES = 'bg-tertiary active:bg-tertiary ';

export const VIEW_TO_LABEL = {
  trace: 'Traces',
  llm: 'LLM Calls',
  errors: 'Errors',
};

export const DEFAULT_FILTER_LS_KEY = 'ls:traces:default-filters';
export const DEFAULT_THREAD_FILTER_LS_KEY = 'ls:threads:default-filters';

export const DEFAULT_FILTERS = ['trace', 'llm', 'errors'];
